/*
 * Copyright (c) 2022. American Express Company
 */

// third-party
import _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import Konva from 'konva';
import { Group } from 'konva/lib/Group';
import { Rect } from 'konva/lib/shapes/Rect';
import { Line } from 'konva/lib/shapes/Line';
import { RegularPolygon } from 'konva/lib/shapes/RegularPolygon';
import { IFrame } from 'konva/lib/types';
// first-party
import { TLElement } from './TLElement';
import { CardModel } from 'src/types/card.model';
import { JaegerEvent } from '../data/JaegerEvent.enum';
import TimelineService from '../services/timeline.service.singleton';
import PinFactory from '../factories/pin.factory.singleton';

export default class TLPin extends TLElement {
    public xPosition: number;
    public yPosition: number;
    public card: CardModel;
    public startDate: LocalDate;
    public endDate: LocalDate;
    public pinSize: number;
    public pinGroup: Group;
    public eventLengthRect: Rect;
    public mainRect: Rect;
    public triangle: RegularPolygon;
    public innerTriangle: RegularPolygon;
    public line: Line;
    public dot: Group;
    public alertIcon: Konva.Group;
    public cardDetails: Konva.Group;
    public savingsDetails: Konva.Group;
    public cardName: Konva.Text;
    public cardDates: Konva.Text;
    public readonly timelineService: TimelineService;
    private readonly pinFactory: PinFactory;

    constructor(xPosition: number, yPosition: number, card: CardModel) {
        super();
        this.timelineService = TimelineService.getInstance();
        this.pinFactory = PinFactory.getInstance();
        this.pinFactory.setParams(this, xPosition, yPosition, card);
        (window as any).bwPins.push({
            card: this.card,
            click: this.pinFactory.onClick.bind(this, this)
        });
    }

    public setDotPosition(): void {
        const dotY: number = this.timelineService.getTimeline().timelineY;
        const dotX: number = Number(this.pinGroup?.attrs?.x) + this.pinSize / 2;
        this.dot?.x(dotX);
        this.dot?.y(dotY);
        this.dot?.show();
    }

    public async updateCardSize(newSize: number): Promise<void> {
        this.card.attributes.icon = newSize;
        const updatedValues: Partial<CardModel> = {
            attributes: this.card.attributes
        };
        await this.timelineService
            .getProps()
            .onJaegerUpdate(this.card, this.timelineService.getProps().user, updatedValues);
        await this.timelineService.onChangeIcon();
    }

    public upsert(xPosition: number, yPosition: number, card: CardModel): void {
        this.pinFactory.update(this, xPosition, yPosition, card);
    }

    public create(): void {
        this.pinFactory.build(this);
    }

    public destroy(): void {
        this.pinGroup.off(JaegerEvent.CLICK);
        this.pinGroup.off(JaegerEvent.DRAG_START);
        this.pinGroup.off(JaegerEvent.DRAG_END);
        this.pinGroup.off(JaegerEvent.DRAG_MOVE);
        this.pinGroup.off(JaegerEvent.CONTEXT_MENU);
        this.pinGroup.off(JaegerEvent.MOUSE_ENTER);
        this.pinGroup.off(JaegerEvent.MOUSE_LEAVE);
        this.pinGroup?.destroyChildren();
        this.pinGroup?.destroy();
        this.line?.destroy();
        this.line = null;
        this.dot?.destroyChildren();
        this.dot?.destroy();
        this.dot = null;
        this.eventLengthRect?.destroy();
        this.eventLengthRect?.destroy();
        this.eventLengthRect = null;
        this.alertIcon?.destroy();
        this.alertIcon = null;
    }

    public remove(): void {}

    public updateX(): void {
        this.pinFactory.updateX(this);
    }

    public updateY(): void {}

    public highlight(pin?: Group): void {
        const PERIOD: number = 250;
        const LIMIT_PIN: number = 1;
        const LIMIT_OTHERS: number = 0.2;
        const pinId: number = pin && _.get(pin, 'attrs.card.id');
        const limit: number = pinId ? LIMIT_OTHERS : LIMIT_PIN;
        const animations: Konva.Animation[] = [];

        this.timelineService.pinBoard.resetAnimation();
        this.timelineService.pinBoard.pins.forEach((pin: TLPin) => {
            const otherPin: Group = pin.pinGroup;
            const otherId: number = _.get(otherPin, 'attrs.card.id');
            if (pinId && pinId === otherId) return;

            const anim = new Konva.Animation((frame: IFrame | undefined) => {
                const othersHighlight: number = pinId
                    ? Math.max(LIMIT_PIN - (LIMIT_PIN - LIMIT_OTHERS) * (frame.time / PERIOD), limit)
                    : Math.min(LIMIT_OTHERS + (LIMIT_PIN - LIMIT_OTHERS) * (frame.time / PERIOD), limit);
                otherPin.opacity(othersHighlight);

                const stopCondition: boolean = pinId ? othersHighlight <= limit : othersHighlight >= limit;
                if (stopCondition) {
                    anim.stop();
                    if (otherPin.opacity() !== limit) otherPin.opacity(limit); // failsafe
                }
            }, this.timelineService.getMainLayer());

            animations.push(anim);
        });

        this.timelineService.pinBoard.animations = animations;
        this.timelineService.pinBoard.animations.forEach((animation: Konva.Animation) => animation.start());
    }
}
